import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { Icon } from '../Icon';

export const Wrapper = styled.div.withConfig({ componentId: 'sc-7df4te-0' })`
    --_bg: var(--c-bg-alt);
    --_br: ${(props) => props.theme.size.borderRadius};

    ${(props) => props.theme.breakpoint.LtMd} {
        display: flex;
        flex-direction: column;
    }
`;

export const TabList = styled.div.attrs(() => ({
  role: 'tablist',
})).withConfig({ componentId: 'sc-7df4te-1' })`
    ${(props) => props.theme.breakpoint.LtMd} {
        display: contents;
    }

    ${(props) => props.theme.breakpoint.Md} {
        display: flex;
        align-items: flex-end;
        overflow: hidden;
    }
`;

interface TabButtonProps {
  $isActive?: boolean;
  $mobileOrder?: number;
  $isFaded?: boolean;
}

export const TabButton = styled(ButtonReset).attrs(() => ({
  role: 'tab',
})).withConfig({ componentId: 'sc-7df4te-2' })<TabButtonProps>`
    --_padding: ${size(1.5)} ${size(2)};

    padding: var(--_padding);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-small-static);

    &[disabled] {
        opacity: 0.37;
        cursor: not-allowed;
    }

    ${(props) => props.theme.breakpoint.LtMd} {
        --_padding: ${size(2)};

        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--_padding);
        background-color: var(--_bg);

        &:not(:first-child) {
            position: relative;

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: var(--_padding);
                right: var(--_padding);
                display: block;
                height: 1px;
                background-color: rgba(0, 0, 0, 0.2);
            }
        }

        ${({ $mobileOrder }) =>
          $mobileOrder &&
          css`
                order: ${$mobileOrder};
            `}

        ${({ $isFaded }) =>
          $isFaded &&
          css`
                color: var(--c-text-muted);
            `}
    }

    ${(props) => props.theme.breakpoint.Md} {
        flex: 0 1 auto;
        min-width: 0;
        border-top-left-radius: var(--_br);
        border-top-right-radius: var(--_br);

        ${({ $isActive }) =>
          $isActive
            ? css`
                      background-color: var(--_bg);
                  `
            : css`
                      &:hover {
                          text-decoration: underline;
                      }
                  `}

        ${({ $isFaded, $isActive }) =>
          $isFaded &&
          !$isActive &&
          css`
                opacity: 0.37;
            `}
    }
`;

export const TabButtonMain = styled.div.withConfig({ componentId: 'sc-7df4te-3' })`
    display: flex;
    align-items: center;
    gap: ${size(0.75)};

    ${Icon} {
        flex-shrink: 0;
    }

    ${(props) => props.theme.breakpoint.LtMd} {
        min-width: 0;
    }
`;

export const TabButtonMainText = styled.div.withConfig({ componentId: 'sc-7df4te-4' })`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

interface TabButtonIconProps {
  $isActive?: boolean;
}

export const TabButtonIcon = styled.div.withConfig({ componentId: 'sc-7df4te-5' })<TabButtonIconProps>`
    font-size: 1.25em;
    margin: -0.625em 0;

    ${(props) => props.theme.breakpoint.Md} {
        display: none;
    }

    ${({ $isActive }) =>
      $isActive &&
      css`
            transform: rotate(180deg);
        `}
`;

interface TabPanelProps {
  $isActive?: boolean;
  $firstInactive?: boolean;
  $mobileOrder?: number;
}

export const TabPanel = styled.div.attrs(() => ({
  role: 'tabpanel',
})).withConfig({ componentId: 'sc-7df4te-6' })<TabPanelProps>`
    --_padding: ${size(3)};

    display: none;
    padding: var(--_padding);
    background-color: var(--_bg);
    border-top-right-radius: var(--_br);
    border-bottom-left-radius: var(--_br);
    border-bottom-right-radius: var(--_br);

    ${(props) => props.theme.breakpoint.LtMd} {
        --_padding: ${size(2)};

        padding-top: 0;

        ${({ $mobileOrder }) =>
          $mobileOrder &&
          css`
                order: ${$mobileOrder};
            `}
    }

    ${({ $isActive }) =>
      $isActive &&
      css`
            display: flex;
            flex-direction: column;
            gap: ${size(2)};
        `}

    ${({ $firstInactive }) =>
      $firstInactive &&
      css`
            border-top-left-radius: var(--_br);
        `}
`;
